import { useState, useCallback } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { 
  IBulletinFormValues, 
  defaultBulletinFormValues, 
  BulletinFormFieldHandler,
  BulletinFormErrors
} from '@/types/admin/bulletins';
import { composeValidators, requiredString, requireIf, validateData } from '@/utils/admin/validation';

export type CreateBulletinFormErrors = BulletinFormErrors;

export const useCreateBulletinForm = () => {
  const [formData, setFormData] = useState<IBulletinFormValues>(defaultBulletinFormValues);
  const [errors, setErrors] = useState<BulletinFormErrors>({});

  const validateForm = useCallback((): boolean => {
    const validator = composeValidators<IBulletinFormValues>(
      requiredString('title', 'Subject'),
      requiredString('message', 'Message'),
      requireIf(
        (data) => data.publishType === 'scheduled',
        'publishTime',
        'Publish date and time'
      ),
    );
    return validateData(formData, setErrors, validator);
  }, [formData]);

  const handleFieldChange: BulletinFormFieldHandler = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing/changing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handlePublishTypeChange = useCallback((type: 'publishNow' | 'draft' | 'scheduled') => {
    setFormData(prev => ({
      ...prev,
      publishType: type,
      publishDate: type === 'publishNow' || type === 'draft' ? null : prev.publishDate || new Date().toISOString(),
      publishTime: type === 'publishNow' || type === 'draft' ? null : prev.publishTime || new Date(),
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(defaultBulletinFormValues);
    setErrors({});
  }, []);

  const showDiscardAlert = useCallback(async (onDiscard: () => void) => {
    const confirmed = await showConfirm(
      'Discard Changes',
      'Are you sure you want to discard your changes?',
      'Discard',
      'Keep Editing'
    );
    
    if (confirmed) {
      onDiscard();
    }
  }, []);

  const hasChanges = useCallback(() => {
    return (
      formData.title !== defaultBulletinFormValues.title ||
      formData.message !== defaultBulletinFormValues.message ||
      formData.publishType !== defaultBulletinFormValues.publishType ||
      formData.highlight !== defaultBulletinFormValues.highlight ||
      formData.sendEmail !== defaultBulletinFormValues.sendEmail ||
      formData.pinHomepage !== defaultBulletinFormValues.pinHomepage ||
      formData.recipientType !== defaultBulletinFormValues.recipientType ||
      (formData.photos && formData.photos.length > 0)
    );
  }, [formData]);

  return {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    resetForm,
    showDiscardAlert,
    hasChanges,
  };
};
